import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isFunction from 'lodash/isFunction'
import map from 'lodash/map'
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgAlert from '@Assets/SVG/Alert'
import SvgHouse from '@Assets/SVG/House'
import SvgHouse2 from '@Assets/SVG/House2'
import SvgPin from '@Assets/SVG/Pin'
import SvgPin3 from '@Assets/SVG/Pin3'
import TruckIcon from '@Assets/SVG/Truck'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { useSetShippingMethodInCartDeliveryMutation } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { CheckoutCartV2ShippingMethod, CheckoutV2ServiceLocation } from '@Pages/onestepcheckout'
import { CollapsableSection } from '@Shared/Components/Sections/CollapsableSection/CollapsableSection'

import styles from './CheckoutShippingStep.module.scss'
import { DeliveryDropPointDialog } from './Components/DeliveryDropPointDialog/DeliveryDropPointDialog'
import { DeliveryInfoDialog } from './Components/DeliveryInfoDialog/DeliveryInfoDialog'
import { DeliveryOptionV2 } from './Components/DeliveryOption/DeliveryOption'
import { DeliveryTab, DeliveryTabs } from './Components/DeliveryTabs/DeliveryTabs'

export interface CheckoutShippingStepProps {
  onErrors?: (errors: string[]) => void
}

export enum CheckoutFlowDeliveryTypes {
  HOME = 'HOME',
  PICKUP = 'PICKUP',
}

export const CheckoutShippingStep: FC<CheckoutShippingStepProps> = () => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const {
    data: {
      cart: {
        availableShippingMethods,
        deliveryAddress,
        shippingMethodID,
      },
    },
    failedNavigation,
    failedNavigationAttempts,
    isRecording,
    model: {
      billingAddress,
      shippingAddress,
    },
    setShippingLoading,
    shippingLoading,
    startRecording,
    stepValidations,
    zipCodes,
  } = useContext(CheckoutFlowContext)

  const [autoSelectedServiceLocation, setAutoSelectedServiceLocation] = useState<Record<string, boolean>>({})
  const [informationDialogContent, setInformationDialogContent] = useState<string | null>(null)
  const [lastClickedShippingMethod, setLastClickedShippingMethod] = useState('')
  const [externalCustomerID, setExternalCustomerID] = useState<Record<string, string>>({})
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [openServiceLocations, setOpenServiceLocations] = useState<CheckoutCartV2ShippingMethod | null>(null)
  const [serviceLocationError, setServiceLocationError] = useState(false)
  const shippingCombinePickupAndHomeV2 = useSplitTest('shipping_combine_pickup_and_home_v2', 'b')
  const soloServicePointShippingMethod = useMemo(
    () => (availableShippingMethods || []).reduce((acc, curr) => (curr.isServiceLocation ? (acc + 1) : acc), 0) === 1
      && (availableShippingMethods || [])?.find(asm => asm.isServiceLocation),
    [availableShippingMethods],
  ) || shippingCombinePickupAndHomeV2
  const selectedShippingMethod = useMemo(() => availableShippingMethods?.find(a => a.id === shippingMethodID), [availableShippingMethods, shippingMethodID])

  const selectedServiceLocation = useMemo(
    () => selectedShippingMethod?.serviceLocations?.find(s => s.externalID === deliveryAddress?.externalID),
    [deliveryAddress?.externalID, selectedShippingMethod?.serviceLocations],
  )
  const [selectedDeliveryTab, setSelectedDeliveryTab] = useState<CheckoutFlowDeliveryTypes | null>(null)

  const defaultSelectedDeliveryTab = useMemo(
    () => selectedDeliveryTab
      || (selectedShippingMethod?.isServiceLocation && !soloServicePointShippingMethod ? CheckoutFlowDeliveryTypes.PICKUP : CheckoutFlowDeliveryTypes.HOME),
    [selectedDeliveryTab, selectedShippingMethod?.isServiceLocation, soloServicePointShippingMethod],
  )
  const [
    setShippingMethodInCartDelivery, {
      error,
      loading,
    },
  ] = useSetShippingMethodInCartDeliveryMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  useEffect(() => {
    setShippingLoading(loading)
  }, [loading, setShippingLoading])
  const deliveryTabOptions = useMemo(() => {
    const options: DeliveryTab[] = []
    if (availableShippingMethods?.some(asm => asm.isCOD || !asm.isServiceLocation)) {
      options.push({
        id: CheckoutFlowDeliveryTypes.HOME,
        title: 'checkout:COF_SELECT_HOME_DELIVERY',
        icon: cofDesignV1 ? <SvgHouse2 /> : <SvgHouse />,
      })
    }
    if (availableShippingMethods?.some(asm => asm.isServiceLocation)) {
      options.push({
        id: CheckoutFlowDeliveryTypes.PICKUP,
        title: 'checkout:COF_SELECT_PICKUP_POINT',
        icon: cofDesignV1 ? <SvgPin3 /> : <SvgPin />,
      })
    }
    return options
  }, [availableShippingMethods, cofDesignV1])

  const showCashOnDelivery = useMemo(
    () => (soloServicePointShippingMethod || defaultSelectedDeliveryTab === CheckoutFlowDeliveryTypes.HOME) && availableShippingMethods?.some(asm => asm.isCOD),
    [availableShippingMethods, defaultSelectedDeliveryTab, soloServicePointShippingMethod],
  )

  useEffect(() => {
    if (failedNavigationAttempts && failedNavigationAttempts > 0 && failedNavigation === CheckoutFlowStep.SHIPPING) {
      if (!selectedShippingMethod) {
        setErrorMessage(t('checkout:COF_HOW_SHOULD_YOUR_ORDER_BE_SHIPPED'))
        document.getElementById('checkout-shipping-section')
          ?.scrollIntoView({ behavior: 'smooth' })
      }
      if (selectedShippingMethod?.isServiceLocation && !selectedServiceLocation) {
        setServiceLocationError(true)
      }
      if (!!selectedShippingMethod?.isExternalCustomerIDRequired && !selectedServiceLocation) {
        setServiceLocationError(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedNavigationAttempts])

  const getServiceLocationsForShippingMethod = useCallback(
    (option?: CheckoutCartV2ShippingMethod) => {
      if (option && availableShippingMethods) {
        return availableShippingMethods?.find(asm => asm.id === option.id)?.serviceLocations || []
      }
      return []
    },
    [availableShippingMethods],
  )

  const selectShippingMethod = useCallback(async (
    allowScroll: boolean,
    shippingMethod: CheckoutCartV2ShippingMethod,
    serviceLocation?: CheckoutV2ServiceLocation,
  ) => {
    if (!stepValidations.ADDRESS || !availableShippingMethods?.find(a => a.id === shippingMethod.id)) {
      return
    }
    setSelectedDeliveryTab(shippingMethod.isServiceLocation ? CheckoutFlowDeliveryTypes.PICKUP : CheckoutFlowDeliveryTypes.HOME)
    ampli.cofDeliveryOptionSelect({
      delivery_option_key: shippingMethod.key,
      delivery_option_legacy_key: shippingMethod.id.replace('postdk_postdk_', ''),
      selected_country: shippingAddress.country || billingAddress.country,
    })
    let newServiceLocation = serviceLocation || selectedServiceLocation
    const invalidServiceLocation = !shippingMethod.serviceLocations?.find(s => s.externalID === newServiceLocation?.externalID)
    if (shippingMethod.isServiceLocation && (
      invalidServiceLocation || (!serviceLocation && !selectedServiceLocation))
    ) {
      newServiceLocation = first(shippingMethod?.serviceLocations)
      setAutoSelectedServiceLocation(prev => ({
        ...prev,
        [shippingMethod.id]: true,
      }))
      ampli.cofDroppointAutoFindRes({ is_success: true })
    } else {
      setAutoSelectedServiceLocation(prev => ({
        ...prev,
        [shippingMethod.id]: false,
      }))
    }
    if (serviceLocationError && newServiceLocation) {
      setServiceLocationError(false)
    }

    const variables = {
      variables: {
        shippingMethod: {
          shippingMethodID: shippingMethod.id,
          serviceLocation: shippingMethod.isServiceLocation && newServiceLocation
            ? {
              externalCustomerID: externalCustomerID[shippingMethod.id] || '',
              additionalStreetInfo: newServiceLocation.additionalStreetInfo || '',
              checksum: newServiceLocation.checksum || '',
              city: newServiceLocation.city || '',
              externalID: newServiceLocation.externalID || '',
              postalCode: newServiceLocation.postalCode || '',
              streetName: newServiceLocation.streetName || '',
              name: newServiceLocation.name || '',
              version: newServiceLocation.version || null,
            }
            : undefined,
        },
      },
    }
    try {
      const res = await setShippingMethodInCartDelivery(variables)
      if (!res.data?.setShippingMethodInCart?.success || !isEmpty(res.errors)) {
        throw new Error('retry save shipping method')
      }
    } catch {
      try {
        const res = await setShippingMethodInCartDelivery(variables)
        if (!res.data?.setShippingMethodInCart?.success || !isEmpty(res.errors)) {
          throw new Error('retry save shipping method')
        }
      } catch {
        try {
          if (!isRecording && typeof window !== 'undefined' && 'hj' in window && isFunction(window.hj)) {
            startRecording()
            window.hj('trigger', 'onestepcheckout_shipping_error')
          }
          const res = await setShippingMethodInCartDelivery(variables)
          if (!res.data?.setShippingMethodInCart?.success || !isEmpty(res.errors)) {
            throw new Error('retry save shipping method')
          }
        } catch (err) {
          console.warn(err)
        }
      }
    }

    if (allowScroll && defaultSelectedDeliveryTab === CheckoutFlowDeliveryTypes.HOME && !shippingMethod.isServiceLocation) {
      document.getElementById('checkout-payment-section')
        ?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [
    availableShippingMethods,
    billingAddress.country,
    externalCustomerID,
    isRecording,
    defaultSelectedDeliveryTab,
    selectedServiceLocation,
    serviceLocationError,
    setShippingMethodInCartDelivery,
    shippingAddress.country,
    startRecording,
    stepValidations.ADDRESS,
  ])

  return (
    <div className={clsx(styles.checkoutShippingStep, cofDesignV1 && styles.cofDesignV1)}>
      <CollapsableSection
        canCollapse={false}
        icon={!cofDesignV1 && <TruckIcon />}
        id="checkout-shipping-section"
        isLoading={shippingLoading || !isEmpty(error)}
        title={t('checkout:COF_CHOOSE_SHIPPING_METHOD')}
      >
        {errorMessage && (
          <p className={styles.error}>
            <SvgAlert />
            {errorMessage}
          </p>
        )}

        {cofDesignV1 && deliveryTabOptions.length > 1 && !soloServicePointShippingMethod
          ? (<DeliveryTabs onSelect={tabOption => setSelectedDeliveryTab(tabOption)} options={deliveryTabOptions} selected={defaultSelectedDeliveryTab} />)
          : (!cofDesignV1 && !soloServicePointShippingMethod)
          && (<DeliveryTabs onSelect={tabOption => setSelectedDeliveryTab(tabOption)} options={deliveryTabOptions} selected={defaultSelectedDeliveryTab} />)}

        <div className={styles.deliveryOptions}>
          {map(availableShippingMethods, option => (
            <DeliveryOptionV2
              key={`${option.id}${option.key}`}
              autoSelectedServiceLocation={!!autoSelectedServiceLocation[option.id]}
              checkError={serviceLocationError}
              className={clsx(option.isUnavailable && styles.orderLast, option.isCOD && styles.orderCOD)}
              deliveryMethod={option}
              disabled={!!option.isUnavailable}
              externalCustomerID={externalCustomerID[option.id]}
              header={(showCashOnDelivery && !soloServicePointShippingMethod && t('checkout:COF_PAY_NOW'))
                || (option.isCOD && t('checkout:COF_CASH_ON_DELIVERY'))
                || undefined}
              hide={!soloServicePointShippingMethod
                && (defaultSelectedDeliveryTab === CheckoutFlowDeliveryTypes.HOME ? !!option.isServiceLocation : !option.isServiceLocation)}
              isSelected={isEqual(shippingMethodID, option.id)}
              loadingDropPoints={loading && lastClickedShippingMethod === `${option.id}${option.key}`}
              onContinue={async () => {
                await selectShippingMethod(false, option)
                document.getElementById('checkout-payment-section')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }}
              onSelect={() => {
                if (isEqual(shippingMethodID, option.id)) {
                  return
                }
                setLastClickedShippingMethod(`${option.id}${option.key}`)
                if (option.isServiceLocation) {
                  ampli.cofDroppointDeliveryMethodSelect()
                }
                selectShippingMethod(true, option)
              }}
              openDropPointDialog={() => {
                setOpenServiceLocations(option)
                ampli.cofDroppointOverlayOpen()
              }}
              openInfoDialog={() => {
                setInformationDialogContent(option.description || null)
                ampli.cofDeliveryMethodInfoOverlayOpen()
              }}
              selectedServiceLocation={selectedServiceLocation}
              setExternalCustomerID={v => setExternalCustomerID(prev => ({
                ...prev,
                [option.id]: v,
              }))}
            />
          ))}
        </div>
      </CollapsableSection>
      <DeliveryInfoDialog info={informationDialogContent} onClose={() => setInformationDialogContent(null)} />
      {openServiceLocations && (
        <DeliveryDropPointDialog
          countryCode={deliveryAddress?.country}
          onClose={() => setOpenServiceLocations(null)}
          onSelect={(shippingMethod, serviceLocation) => {
            selectShippingMethod(false, shippingMethod, serviceLocation)
            setOpenServiceLocations(null)
          }}
          selectedServiceLocationId={selectedServiceLocation?.externalID || undefined}
          serviceLocations={getServiceLocationsForShippingMethod(selectedShippingMethod)}
          shippingMethod={openServiceLocations}
          zipCodes={zipCodes}
        />
      )}
    </div>
  )
}

